import React, { useEffect, useState } from 'react';
import useAxios from 'axios-hooks';
import { Checkbox, MessageBarType, PrimaryButton } from '@fluentui/react';
import { Form, Formik } from 'formik';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import TextField from '../../../Forms/TextField';
import Toast from '../../../Forms/Toast';
import FeatureSummary from '../../../Util/FeatureSummary';
import MessageBarComponent from '../../../Util/MessageBarComponent';

const newEmlSenderObject = {
    emlSenderID: 0,
    fromName: '',
    fromEmailAddress: '',
    replyToEmail: '',
    streetAddress1: '2163 Newcastle Avenue, Suite 100',
    city: 'Cardiff by the Sea',
    state: 'CA',
    zipCode: '92007',
    addToSendGrid: false,
};

const WizardPage = () => {
    const [showToast, setShowToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const history = useHistory();
    const location = useLocation();
    const { emlSenderId: idFromParams } = useParams();

    // Carried through the redirect below, since creating remounts this page on the edit route.
    const [unverifiedEmail, setUnverifiedEmail] = useState(location.state?.unverifiedEmail ?? null);
    const [sendGridError, setSendGridError] = useState(location.state?.sendGridError ?? null);

    const [{ data: emlSender }, getEmlSender] = useAxios(
        { url: `/emails/email-sender/${idFromParams}`, method: 'GET' },
        { manual: true }
    );

    useEffect(() => {
        if (idFromParams) {
            getEmlSender();
        }
    }, [idFromParams]);

    const [{ loading: submitting }, submitForm] = useAxios(
        { url: `/emails/email-sender` },
        { manual: true }
    );

    return (
        <Formik
            initialValues={emlSender || newEmlSenderObject}
            onSubmit={async (values) => {
                setErrorMessage(null);
                const isUpdate = !!idFromParams;

                // axios-hooks rejects on a non-2xx response, so this must be caught rather than inspected.
                try {
                    const result = await submitForm({
                        url: "/emails/email-sender",
                        method: isUpdate ? "PUT" : "POST",
                        data: values,
                    });

                    if (!idFromParams && result.data?.emlSenderID) {
                        const pendingEmail = result.data.sendGridVerified === false
                            ? result.data.fromEmailAddress
                            : null;
                        const sgError = result.data.sendGridError || null;

                        setUnverifiedEmail(pendingEmail);
                        setSendGridError(sgError);
                        history.push(
                            `/email-senders-feature/${result.data.emlSenderID}/wizard`,
                            { unverifiedEmail: pendingEmail, sendGridError: sgError }
                        );
                    }
                    setShowToast(true);
                } catch (err) {
                    const apiErrors = err?.response?.data?.errors;
                    setErrorMessage(apiErrors?.length
                        ? apiErrors.map(e => e.errorMessage).join(' ')
                        : 'An error occurred saving the email sender.');
                }
            }}
            enableReinitialize
            isInitialValid={false}
        >
            {({ errors, values, setFieldValue, handleSubmit, dirty }) => {
                const isButtonDisabled = !dirty || !values.fromName || !values.fromEmailAddress;

                return (
                    <Form style={{ maxWidth: "500px", marginBottom: 500 }}>
                        <div className="displayflex mb20" style={{ justifyContent: 'space-between' }}>
                            <h4>Email Sender</h4>
                            <PrimaryButton
                                onClick={handleSubmit}
                                text={idFromParams ? "Update" : 'Create'}
                                disabled={isButtonDisabled}
                            />
                        </div>

                        <MessageBarComponent
                            messageType={MessageBarType.error}
                            showMessage={!!errorMessage}
                            setShowMessage={() => setErrorMessage(null)}
                            isMultiline
                            message={errorMessage}
                        />

                        <MessageBarComponent
                            messageType={MessageBarType.severeWarning}
                            showMessage={!!sendGridError}
                            setShowMessage={() => setSendGridError(null)}
                            isMultiline
                            message={`This sender was saved, but could not be added to SendGrid, so it cannot send email yet. Add it in SendGrid manually. (${sendGridError})`}
                        />

                        <MessageBarComponent
                            messageType={MessageBarType.warning}
                            showMessage={!!unverifiedEmail}
                            setShowMessage={() => setUnverifiedEmail(null)}
                            isMultiline
                            message={`This sender was added to SendGrid but is not verified yet, so it cannot send email. SendGrid emailed a verification link to ${unverifiedEmail} — that link must be clicked to finish setup.`}
                        />

                        <TextField
                            label="From Name"
                            className="mt10"
                            name="fromName"
                            value={values.fromName}
                            onChange={(e, v) => setFieldValue('fromName', v)}
                            errorMessage={errors.fromName}
                            required
                        />

                        <TextField
                            label="From Email Address"
                            className="mt10"
                            name="fromEmailAddress"
                            value={values.fromEmailAddress}
                            onChange={(e, v) => setFieldValue('fromEmailAddress', v)}
                            errorMessage={errors.fromEmailAddress}
                            required
                        />

                        <TextField
                            label="Reply To Email"
                            className="mt10"
                            name="replyToEmail"
                            value={values.replyToEmail}
                            onChange={(e, v) => setFieldValue('replyToEmail', v)}
                            errorMessage={errors.replyToEmail}
                        />

                        <TextField
                            label="Street Address"
                            className="mt10"
                            name="streetAddress1"
                            value={values.streetAddress1}
                            onChange={(e, v) => setFieldValue('streetAddress1', v)}
                            errorMessage={errors.streetAddress1}
                        />

                        <TextField
                            label="City"
                            className="mt10"
                            name="city"
                            value={values.city}
                            onChange={(e, v) => setFieldValue('city', v)}
                            errorMessage={errors.city}
                        />

                        <TextField
                            label="State"
                            className="mt10"
                            name="state"
                            value={values.state}
                            onChange={(e, v) => setFieldValue('state', v)}
                            errorMessage={errors.state}
                        />

                        <TextField
                            label="Zip Code"
                            className="mt10"
                            name="zipCode"
                            value={values.zipCode}
                            onChange={(e, v) => setFieldValue('zipCode', v)}
                            errorMessage={errors.zipCode}
                        />

                        {!idFromParams && (
                            <Checkbox
                                label="Also create this sender in SendGrid"
                                className="mt20"
                                name="addToSendGrid"
                                checked={!!values.addToSendGrid}
                                onChange={(e, checked) => setFieldValue('addToSendGrid', !!checked)}
                            />
                        )}

                        <Toast show={showToast} message="Saved!" onClose={() => setShowToast(false)} />
                        <FeatureSummary
                            label="About Email Senders"
                            summary="Configure global email sender profiles with from name, email address, reply-to address, and physical mailing address."
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

export default WizardPage;
